// Instagram-канал (Meta, Instagram Login / Graph API) — сервис API.
// Базовый префикс: /api/v1/integrations/providers/instagram
// Контракт: backend handoff (14 REST-эндпоинтов).
//
// Ошибки backend — простой формат { detail: string } (без структурного кода).
// Бросаем типизированный InstagramApiError(status + detail), чтобы будущий
// errorParser (Unit 4) мог классифицировать по статусу/анкорам. detail
// прокидывается verbatim в message.

import authService from './auth.service';
import { buildQuery } from '../utils/buildQuery';
import type {
  InstagramInstanceOut,
  InstagramInstanceCreateIn,
  DeleteInstanceResult,
  InstagramOAuthStartOut,
  InstagramChatOut,
  InstagramChatPatchIn,
  InstagramChatCloseIn,
  MarkReadResult,
  InstagramCloseReasonOut,
  InstagramMessageOut,
  InstagramSendTextIn,
  InstagramCommentReplyIn,
  InstagramPrivateReplyIn,
} from '../types/instagram.types';

/** Ошибка Instagram-API: несёт HTTP-статус и verbatim detail для парсера/UI. */
export class InstagramApiError extends Error {
  readonly status?: number;
  readonly detail: string;

  constructor(detail: string, status?: number) {
    super(detail);
    this.name = 'InstagramApiError';
    this.detail = detail;
    this.status = status;
  }
}

/** Достаёт detail из тела ответа ({ detail: string } или Pydantic-массив). */
async function extractDetail(res: Response): Promise<string> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // body — не JSON
  }
  const detail = (body as { detail?: unknown } | null | undefined)?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d as { msg?: unknown })?.msg)
      .filter((m): m is string => typeof m === 'string');
    if (msgs.length) return msgs.join('; ');
  }
  return res.statusText || 'Неизвестная ошибка';
}

class InstagramService {
  private readonly BASE = '/api/v1/integrations/providers/instagram';

  // ─────────────── Инстансы (каналы) ───────────────

  // Список каналов организации. is_active default true. scope read.
  async listInstances(opts?: { is_active?: boolean }): Promise<InstagramInstanceOut[]> {
    const is_active = opts?.is_active ?? true;
    return this.request<InstagramInstanceOut[]>(
      `${this.BASE}/instances${buildQuery({ is_active })}`,
    );
  }

  // Шаг 0 онбординга: создать канал. scope manage.
  async createInstance(body: InstagramInstanceCreateIn): Promise<InstagramInstanceOut> {
    return this.request<InstagramInstanceOut>(`${this.BASE}/instances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Удалить канал ЦЕЛИКОМ (каскад: чаты, credentials, entity_links).
  // НЕОБРАТИМО — caller обязан показать строгий confirm. scope manage.
  async deleteInstance(instanceId: number): Promise<DeleteInstanceResult> {
    return this.request<DeleteInstanceResult>(`${this.BASE}/instances/${instanceId}`, {
      method: 'DELETE',
    });
  }

  // ─────────────── OAuth (онбординг) ───────────────

  // Шаг 1: получить authorize_url для редиректа в Instagram Login. scope manage.
  async startOAuth(instanceId: number): Promise<InstagramOAuthStartOut> {
    return this.request<InstagramOAuthStartOut>(
      `${this.BASE}/instances/${instanceId}/oauth/start`,
      { method: 'POST' },
    );
  }

  // ─────────────── Чаты (инбокс) ───────────────

  // status_filter: одно значение или CSV. Сортировка last_message_at DESC. scope read.
  async listChats(
    instanceId: number,
    opts?: { status_filter?: string | null; limit?: number; offset?: number },
  ): Promise<InstagramChatOut[]> {
    return this.request<InstagramChatOut[]>(
      `${this.BASE}/instances/${instanceId}/chats${buildQuery({
        status_filter: opts?.status_filter,
        limit: opts?.limit,
        offset: opts?.offset,
      })}`,
    );
  }

  // Привязка клиента / назначение оператора. scope manage.
  async patchChat(chatId: number, body: InstagramChatPatchIn): Promise<InstagramChatOut> {
    return this.request<InstagramChatOut>(`${this.BASE}/chats/${chatId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Сбросить unread (оператор открыл чат). scope read.
  async markRead(chatId: number): Promise<MarkReadResult> {
    return this.request<MarkReadResult>(`${this.BASE}/chats/${chatId}/read`, {
      method: 'POST',
    });
  }

  // Тело опционально целиком. scope manage.
  async closeChat(chatId: number, body?: InstagramChatCloseIn): Promise<InstagramChatOut> {
    return this.request<InstagramChatOut>(`${this.BASE}/chats/${chatId}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
  }

  // scope manage.
  async reopenChat(chatId: number): Promise<InstagramChatOut> {
    return this.request<InstagramChatOut>(`${this.BASE}/chats/${chatId}/reopen`, {
      method: 'POST',
    });
  }

  // Справочник причин закрытия. scope read.
  async listCloseReasons(): Promise<InstagramCloseReasonOut[]> {
    return this.request<InstagramCloseReasonOut[]>(`${this.BASE}/close-reasons`);
  }

  // ─────────────── Сообщения ───────────────

  // История по НАШЕМУ int id чата. timestamp DESC (новые первыми). scope read.
  async listMessages(
    chatId: number,
    opts?: { limit?: number; offset?: number },
  ): Promise<InstagramMessageOut[]> {
    return this.request<InstagramMessageOut[]>(
      `${this.BASE}/chats/${chatId}/messages${buildQuery({
        limit: opts?.limit,
        offset: opts?.offset,
      })}`,
    );
  }

  // Свободный текст (1..1000). Внутри 24ч-окна. scope send.
  async sendText(chatId: number, body: InstagramSendTextIn): Promise<InstagramMessageOut> {
    return this.request<InstagramMessageOut>(`${this.BASE}/chats/${chatId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // Публичный ответ на комментарий (1..2200). scope send.
  async commentReply(
    messageId: number,
    body: InstagramCommentReplyIn,
  ): Promise<InstagramMessageOut> {
    return this.request<InstagramMessageOut>(
      `${this.BASE}/messages/${messageId}/comment-reply`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  // Приватный ответ в директ на комментарий (1..1000). scope send.
  async privateReply(
    messageId: number,
    body: InstagramPrivateReplyIn,
  ): Promise<InstagramMessageOut> {
    return this.request<InstagramMessageOut>(
      `${this.BASE}/messages/${messageId}/private-reply`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  // ─────────────── private ───────────────

  // Сетевая ошибка → InstagramApiError (без статуса), !ok → detail-парсер,
  // 204 → undefined.
  private async request<T>(url: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await authService.fetchWithAuth(url, init);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Нет соединения с сервером';
      throw new InstagramApiError(msg);
    }

    if (!res.ok) {
      throw new InstagramApiError(await extractDetail(res), res.status);
    }

    if (res.status === 204) {
      return undefined as unknown as T;
    }
    return (await res.json()) as T;
  }
}

const instagramService = new InstagramService();
export default instagramService;
